import { useCallback, useEffect, useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getActorDisplayName, REPORT_STATUS_LABELS } from '@safetyhub/shared';
import { Card, Modal, SUB_CONFIRM_LAYER, useConfirmDialog } from '@safetyhub/ui';
import { adminApi, extractApiError, toAbsoluteApiFileUrl } from '../lib/api';
import {
  ML_PHOTO_GRADES,
  PHOTO_GRADE_UNKNOWN_KEY,
  SOURCE_REPORT_DELETED_LABEL,
  type MlPhotoGrade,
  type PhotoPurpose,
} from '../lib/ml-sources';
import { useToastStore } from '../stores/toast';
import { ImageLightbox } from '../components/ImageLightbox';
import { PhotoGradeBadge } from '../components/PhotoGradeBadge';
import { MlPhotoThumbnail } from '../components/MlPhotoThumbnail';

/** 결재 상황 모달(z-[80]) 위에서 여는 확인 — 그보다 한 층 위. */
const APPROVAL_CONFIRM_LAYER = {
  overlayZClassName: 'z-[90]',
  overlayBgClassName: 'bg-black/25',
} as const;

// 결재 상태별 색상
const approvalStatusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
};

const approvalStatusLabels: Record<string, string> = {
  PENDING: '대기중',
  APPROVED: '승인',
  REJECTED: '반려',
};

// 상태별 색상
const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  REVIEWING: 'bg-yellow-100 text-yellow-800',
  COMPLETED: 'bg-green-100 text-green-800',
};

// PDF 상태별 색상
const pdfStatusColors: Record<string, string> = {
  NONE: 'bg-gray-100 text-gray-600',
  PENDING: 'bg-yellow-100 text-yellow-700',
  GENERATING: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
};

// 상태 레이블
/**
 * 점검 상태 라벨 — 키는 Prisma `enum InspectionStatus`와 **전수 일치**해야 한다.
 *
 * ⚠️ 호출부가 `statusLabels[x] || x`로 폴백하기 때문에, 키가 빠지면 에러가 아니라
 * **화면에 raw enum이 그대로 노출**된다(2026-07-28 dev 실측에서 목록에 `SCHEDULED`가
 * 영문으로 찍혀 있는 것을 발견). 존재하지 않는 키(`REVIEWING`)가 섞여 있어도 마찬가지로
 * 조용하다 — 실제 enum은 `PENDING_REVIEW`다.
 *
 * 문구는 admin 기존 것을 유지한다 — web(`inspectionStatusConfig`)은 DRAFT를 '진행중'으로
 * 합치지만, admin은 운영 콘솔이라 '초안'과 '진행중'을 나눠 보는 편이 정보가 더 많다.
 * 신규 `SCHEDULED`만 web 문구('예약됨')를 따랐다. enum에 값을 추가하면 여기도 함께 추가할 것.
 */
const statusLabels: Record<string, string> = {
  SCHEDULED: '예약됨',
  DRAFT: '초안',
  IN_PROGRESS: '진행중',
  PENDING_REVIEW: '검토중',
  COMPLETED: '완료',
};

const pdfStatusLabels: Record<string, string> = {
  NONE: '미생성',
  PENDING: '대기중',
  GENERATING: '생성중',
  COMPLETED: '완료',
  FAILED: '실패',
};

// (보고서 타입 레이블 맵을 두지 않는다 — 점검 상세의 보고서 행은 제목으로 식별한다.)

// 보고서 상태 레이블 및 색상
/**
 * 보고서 상태 라벨 — shared 정본(`REPORT_STATUS_LABELS`) 파생.
 *
 * 운영 콘솔 목록은 공백 없는 간결체(`결재 대기`가 아니라 `결재대기`)를 쓰고 `승인`처럼
 * 짧게 부르므로 DRAFT를 뺀 전부를 덮어쓴다. spread를 남겨 두는 이유는 문구가 아니라
 * **키 전수 보장** — enum에 상태가 추가돼도 `|| report.status` 폴백으로 raw enum이
 * 조용히 새는 일(2026-07-28 실사고)이 반복되지 않는다.
 */
const reportStatusLabels: Record<string, string> = {
  ...REPORT_STATUS_LABELS,
  CREATING: '생성중',
  PENDING_APPROVAL: '결재대기',
  APPROVED: '승인',
  REJECTED: '반려',
  CANCELLED: '취소',
};

const reportStatusColors: Record<string, string> = {
  CREATING: 'bg-gray-100 text-gray-700',
  DRAFT: 'bg-gray-100 text-gray-700',
  PENDING_APPROVAL: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
};

interface Inspection {
  id: string;
  status: string;
  date: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  organization: { id: string; name: string; category: string };
  site: { id: string; name: string };
  // 서버 응답: inspector는 user 레코드 (member 중첩)
  inspector: { id: string; deletedAt: string | null; member: { name: string } | null };
  report: {
    id: string;
    title: string;
    status: string;
    pdfStatus: string;
    fileUrl: string | null;
    pdfError: string | null;
  } | null;
  _count: { photos: number; collaborators: number };
  sequenceNumber: number;
}

interface ReportItem {
  id: string;
  type: string;
  title: string;
  status: string;
  pdfStatus: string;
  fileUrl: string | null;
  pdfError: string | null;
  createdAt: string;
}

interface InspectionDetail {
  id: string;
  status: string;
  date: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  organization: { id: string; name: string; category: string };
  site: { id: string; name: string; address: string | null };
  // 서버 응답: inspector는 user 레코드 (member 중첩)
  inspector: { id: string; deletedAt: string | null; member: { name: string } | null };
  reports: ReportItem[];
  report: ReportItem | null;
  /**
   * ⚠️ 이 화면은 2026-10-06까지 사진을 **받기만 하고 그리지 않았다**(렌더 코드 0건, 통계 숫자만).
   * P5가 「운영자가 같은 점검을 보면 지운 사진도 보이고 각 사진에 품질 등급이 표시된다」를
   * 요구해 2026-10-07에 사진 섹션을 신설했다 — `deletedAt`·`grade`·`purpose`는 그때 더해진 것이다.
   */
  photos: Array<{
    id: string;
    url: string;
    thumbnailUrl: string | null;
    analysisStatus: string;
    createdAt: string;
    deletedAt: string | null;
    grade: MlPhotoGrade;
    purpose: PhotoPurpose;
  }>;
  /** 이 점검에서 나온 개선 이미지(P11). `sourceReportId` null = 출처 보고서 삭제됨. */
  improvementImages: Array<{
    id: string;
    imageUrl: string;
    mimeType: string;
    summaryText: string;
    aiModel: string;
    usageCount: number;
    createdAt: string;
    sourceReportId: string | null;
    sourcePhotoId: string | null;
    hasEmbedding: boolean;
  }>;
  collaborators: Array<{
    id: string;
    role: string;
    user: { id: string; deletedAt?: string | null; member: { name: string } | null };
  }>;
  _count: { photos: number; findings: number };
  sequenceNumber: number;
}

interface ApprovalSignature {
  id: string;
  signerName: string;
  signerTitle: string | null;
  approvalLevel: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  signedAt: string | null;
  comment: string | null;
}

interface ApprovalRound {
  round: number;
  signatures: ApprovalSignature[];
}

interface ApprovalStatusData {
  reportId: string;
  reportTitle: string;
  reportStatus: string;
  rounds: ApprovalRound[];
}

export default function Inspections() {
  const [selectedInspection, setSelectedInspection] = useState<string | null>(null);
  const [approvalModalReportId, setApprovalModalReportId] = useState<string | null>(null);
  const [expandedRounds, setExpandedRounds] = useState<Set<number>>(new Set());
  const [confirm, confirmDialog] = useConfirmDialog();
  // 상세 안에서 열린 사진 라이트박스 수 — 라이트박스는 window keydown으로 Esc를 받고 base Modal은
  // document keydown이라 **둘 다** 돈다(document가 먼저). 라이트박스가 떠 있는 동안 상세의 Esc를 꺼야
  // Esc 한 번에 라이트박스만 닫힌다(종전 Overlay엔 Esc가 없어 상세가 함께 닫히는 일이 없었다).
  const [openLightboxCount, setOpenLightboxCount] = useState(0);
  const handleLightboxOpenChange = useCallback((open: boolean) => {
    setOpenLightboxCount((n) => Math.max(0, n + (open ? 1 : -1)));
  }, []);
  const [page, setPage] = useState(0);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const limit = 20;
  const { showToast } = useToastStore();
  const queryClient = useQueryClient();

  // 점검 목록 조회
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'inspections', page, includeDeleted],
    queryFn: () => adminApi.getInspections({ limit, offset: page * limit, includeDeleted }),
  });

  // 점검 상세 조회
  const { data: detailData, isLoading: isDetailLoading } = useQuery({
    queryKey: ['admin', 'inspection', selectedInspection],
    queryFn: () => adminApi.getInspection(selectedInspection!),
    enabled: !!selectedInspection,
  });

  // PDF 재생성 mutation (보고서 ID로)
  const resetPdfMutation = useMutation({
    mutationFn: (reportId: string) => adminApi.resetReportPdf(reportId),
    onSuccess: () => {
      showToast('PDF 재생성이 시작되었습니다.', 'success');
      queryClient.invalidateQueries({ queryKey: ['admin', 'inspections'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'inspection', selectedInspection] });
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    },
  });

  // PDF 상태 초기화 mutation (테스트용)
  const resetPdfStatusMutation = useMutation({
    mutationFn: ({ reportId, status }: { reportId: string; status: 'NONE' | 'PENDING' | 'GENERATING' | 'COMPLETED' | 'FAILED' }) =>
      adminApi.resetPdfStatus(reportId, status),
    onSuccess: (response) => {
      showToast(`PDF 상태가 ${response.data?.newStatus}로 초기화되었습니다.`, 'success');
      queryClient.invalidateQueries({ queryKey: ['admin', 'inspections'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'inspection', selectedInspection] });
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    },
  });

  // 결재 상황 조회
  const { data: approvalData, isLoading: isApprovalLoading } = useQuery({
    queryKey: ['admin', 'report-approvals', approvalModalReportId],
    queryFn: () => adminApi.getReportApprovals(approvalModalReportId!),
    enabled: !!approvalModalReportId,
  });

  // 결재 초기화 mutation
  const resetApprovalMutation = useMutation({
    mutationFn: (signatureId: string) => adminApi.resetApprovalSignature(signatureId),
    onSuccess: () => {
      showToast('결재 상태가 초기화되었습니다.', 'success');
      queryClient.invalidateQueries({ queryKey: ['admin', 'report-approvals', approvalModalReportId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'inspections'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'inspection', selectedInspection] });
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    },
  });

  // 결재 라운드 삭제 mutation
  const deleteRoundMutation = useMutation({
    mutationFn: ({ reportId, round }: { reportId: string; round: number }) =>
      adminApi.deleteApprovalRound(reportId, round),
    onSuccess: () => {
      showToast('결재 라운드가 삭제되었습니다.', 'success');
      queryClient.invalidateQueries({ queryKey: ['admin', 'report-approvals', approvalModalReportId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'inspections'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'inspection', selectedInspection] });
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    },
  });

  const inspections = (data?.data || []) as Inspection[];
  const total = data?.meta?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const inspection = detailData?.data as InspectionDetail | undefined;

  const handleRowClick = (id: string) => {
    setSelectedInspection(id);
  };

  const handleCloseModal = () => {
    setSelectedInspection(null);
  };

  // 아래 넷의 확인 층: 점검 상세(z-[70]) 안이면 z-[80](`SUB_CONFIRM_LAYER`), 그 위의 결재 상황(z-[80])
  // 안이면 z-[90](`APPROVAL_CONFIRM_LAYER`) — 층은 여는 쪽이 정한다(z 정책 70→80→90→100).
  const handleResetPdf = async (reportId: string) => {
    if (
      !(await confirm({
        title: 'PDF 재생성',
        body: 'PDF를 재생성하시겠습니까? 기존 PDF 파일이 삭제되고 새로 생성됩니다.',
        confirmLabel: '재생성',
        danger: true,
        ...SUB_CONFIRM_LAYER,
      }))
    )
      return;
    resetPdfMutation.mutate(reportId);
  };

  const handleOpenApprovalModal = (reportId: string) => {
    setApprovalModalReportId(reportId);
    setExpandedRounds(new Set([1])); // 첫 번째 라운드 (최신) 자동 펼침
  };

  const handleCloseApprovalModal = () => {
    setApprovalModalReportId(null);
    setExpandedRounds(new Set());
  };

  const handleToggleRound = (round: number) => {
    setExpandedRounds((prev) => {
      const next = new Set(prev);
      if (next.has(round)) {
        next.delete(round);
      } else {
        next.add(round);
      }
      return next;
    });
  };

  const handleResetApproval = async (signatureId: string) => {
    if (
      !(await confirm({
        title: '결재 초기화',
        body: '이 결재를 초기화하시겠습니까? 서명과 코멘트가 삭제됩니다.',
        confirmLabel: '초기화',
        danger: true,
        ...APPROVAL_CONFIRM_LAYER,
      }))
    )
      return;
    resetApprovalMutation.mutate(signatureId);
  };

  const handleDeleteRound = async (reportId: string, round: number) => {
    if (
      !(await confirm({
        title: '라운드 삭제',
        body: `${round}번째 라운드를 삭제하시겠습니까? 해당 라운드의 모든 결재 정보가 영구 삭제됩니다.`,
        confirmLabel: '삭제',
        danger: true,
        ...APPROVAL_CONFIRM_LAYER,
      }))
    )
      return;
    deleteRoundMutation.mutate({ reportId, round });
  };

  // 결재 초기화 버튼 활성화 여부 확인
  const canResetSignature = (signature: ApprovalSignature, allSignatures: ApprovalSignature[]): boolean => {
    // 이미 PENDING이면 비활성화
    if (signature.status === 'PENDING') return false;

    // 더 높은 레벨에 처리된 결재가 있으면 비활성화
    const higherLevelProcessed = allSignatures.some(
      (s) => s.approvalLevel > signature.approvalLevel && s.status !== 'PENDING'
    );

    return !higherLevelProcessed;
  };

  if (error) {
    return (
      <div className="p-4 text-red-600">
        오류: {extractApiError(error).message}
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">점검 관리</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => {
                setIncludeDeleted(e.target.checked);
                setPage(0); // 페이지 초기화
              }}
              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            삭제된 점검 보기
          </label>
          <div className="text-sm text-gray-500">
            총 {total.toLocaleString()}건
          </div>
        </div>
      </div>

      {/* 점검 목록 테이블 */}
      <Card padding="none" radius="lg" className="overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                점검 날짜
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                점검명
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                담당자
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                상태
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                생성일자
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  로딩 중...
                </td>
              </tr>
            ) : inspections.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  점검이 없습니다.
                </td>
              </tr>
            ) : (
              inspections.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => handleRowClick(item.id)}
                  className={`hover:bg-gray-50 cursor-pointer ${
                    item.deletedAt ? 'bg-red-50' : ''
                  }`}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {new Date(item.date).toLocaleDateString('ko-KR')}
                    {item.deletedAt && (
                      <span className="ml-2 text-xs text-red-500">(삭제됨)</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {item.site.name} {item.sequenceNumber}차 점검
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {getActorDisplayName({ name: item.inspector.member?.name, deletedAt: item.inspector.deletedAt })}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        statusColors[item.status] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {statusLabels[item.status] || item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {new Date(item.createdAt).toLocaleDateString('ko-KR')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <span className="text-sm text-gray-600">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        )}
      </Card>

      {/* 상세 모달 */}
      {selectedInspection && (
        // 종전의 sticky 헤더 + 패널 스크롤은 base 본문 스크롤(헤더 고정)이 맡는다.
        <Modal
          open
          onClose={handleCloseModal}
          title="점검 상세"
          size="xl"
          closeOnEsc={openLightboxCount === 0}
        >
            <div>
              {isDetailLoading ? (
                <div className="text-center py-8 text-gray-500">로딩 중...</div>
              ) : inspection ? (
                <div className="space-y-6">
                  {/* 기본 정보 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">점검 ID</label>
                      <div className="text-sm font-mono text-gray-600">{inspection.id}</div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">점검명</label>
                      <div className="text-sm font-semibold">{inspection.site.name} {inspection.sequenceNumber}차 점검</div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">점검 날짜</label>
                      <div className="text-sm">{new Date(inspection.date).toLocaleDateString('ko-KR', { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit' })}</div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">조직</label>
                      <div className="text-sm">{inspection.organization.name}</div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">현장</label>
                      <div className="text-sm">{inspection.site.name}</div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">담당자</label>
                      <div className="text-sm">{getActorDisplayName({ name: inspection.inspector.member?.name, deletedAt: inspection.inspector.deletedAt })}</div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">상태</label>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          statusColors[inspection.status] || 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {statusLabels[inspection.status] || inspection.status}
                      </span>
                    </div>
                  </div>

                  {/* 통계 */}
                  <div className="grid grid-cols-3 gap-4 bg-gray-50 rounded-card p-4">
                    <div className="text-center">
                      <div className="text-2xl font-semibold text-gray-900">{inspection._count.photos}</div>
                      <div className="text-xs text-gray-500">사진 수</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-semibold text-gray-900">{inspection._count.findings}</div>
                      <div className="text-xs text-gray-500">발견사항</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-semibold text-gray-900">{inspection.collaborators?.length ?? 0}</div>
                      <div className="text-xs text-gray-500">참여자</div>
                    </div>
                  </div>

                  {/* 사진 — 지운 것도 보여주고 품질 등급으로 가른다(P5) */}
                  <InspectionPhotosSection
                    photos={inspection.photos ?? []}
                    onLightboxOpenChange={handleLightboxOpenChange}
                  />

                  {/* 개선 이미지 — 이 점검에서 나온 것(P11) */}
                  <InspectionImprovementImagesSection
                    images={inspection.improvementImages ?? []}
                    onLightboxOpenChange={handleLightboxOpenChange}
                  />

                  {/* 보고서 목록 */}
                  <div className="border rounded-card p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">보고서 목록</h3>
                    {inspection.reports && inspection.reports.length > 0 ? (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 text-xs text-gray-500 font-medium">제목</th>
                            <th className="text-left py-2 text-xs text-gray-500 font-medium">보고서 상태</th>
                            <th className="text-left py-2 text-xs text-gray-500 font-medium">PDF 상태</th>
                            <th className="text-right py-2 text-xs text-gray-500 font-medium">액션</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inspection.reports.map((report) => (
                            <tr key={report.id} className="border-b last:border-b-0">
                              <td className="py-2">
                                <span className="text-gray-900">{report.title}</span>
                              </td>
                              <td className="py-2">
                                <span
                                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                                    reportStatusColors[report.status] || 'bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  {reportStatusLabels[report.status] || report.status}
                                </span>
                              </td>
                              <td className="py-2">
                                <span
                                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                                    pdfStatusColors[report.pdfStatus] || 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {pdfStatusLabels[report.pdfStatus] || report.pdfStatus}
                                </span>
                                {report.pdfError && (
                                  <span className="ml-1 text-xs text-red-500" title={report.pdfError}>⚠</span>
                                )}
                              </td>
                              <td className="py-2 text-right space-x-1">
                                {/* PDF 상태 초기화 (테스트용) */}
                                <select
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={async (e) => {
                                    const status = e.target.value as 'NONE' | 'PENDING' | 'GENERATING' | 'COMPLETED' | 'FAILED';
                                    // 선택을 먼저 되돌린다 — 종전엔 동기 confirm 뒤에 되돌렸는데, 확인 창이
                                    // 비동기라 기다리는 동안 select가 고른 값을 보이게 된다.
                                    e.target.value = '';
                                    if (
                                      status &&
                                      (await confirm({
                                        title: 'PDF 상태 초기화',
                                        body: `PDF 상태를 ${status}로 초기화하시겠습니까?\n(PDF 파일이 있다면 삭제됩니다)`,
                                        confirmLabel: '초기화',
                                        danger: true,
                                        ...SUB_CONFIRM_LAYER,
                                      }))
                                    ) {
                                      resetPdfStatusMutation.mutate({ reportId: report.id, status });
                                    }
                                  }}
                                  disabled={resetPdfStatusMutation.isPending}
                                  className="px-1 py-1 text-xs text-gray-600 border rounded bg-white disabled:opacity-50"
                                  title="PDF 상태 초기화 (테스트용)"
                                  defaultValue=""
                                >
                                  <option value="" disabled>PDF 초기화</option>
                                  <option value="NONE">NONE</option>
                                  <option value="PENDING">PENDING</option>
                                  <option value="GENERATING">GENERATING</option>
                                  <option value="COMPLETED">COMPLETED</option>
                                  <option value="FAILED">FAILED</option>
                                </select>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleResetPdf(report.id);
                                  }}
                                  disabled={resetPdfMutation.isPending || report.status !== 'APPROVED'}
                                  className="px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={report.status !== 'APPROVED' ? '결재 완료된 보고서만 PDF 재생성 가능' : 'PDF 재생성 (세션 PDF 포함)'}
                                >
                                  PDF 재생성
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenApprovalModal(report.id);
                                  }}
                                  className="px-2 py-1 text-xs text-brand-600 hover:text-brand-800 hover:bg-brand-50 rounded"
                                  title="결재 상황 보기"
                                >
                                  결재 상황
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="text-sm text-gray-500 text-center py-4">
                        생성된 보고서가 없습니다.
                      </div>
                    )}
                  </div>

                  {/* 삭제 정보 */}
                  {inspection.deletedAt && (
                    <div className="bg-red-50 border border-red-200 rounded-card p-4">
                      <div className="text-sm text-red-700">
                        <span className="font-semibold">삭제됨:</span>{' '}
                        {new Date(inspection.deletedAt).toLocaleString('ko-KR')}
                      </div>
                    </div>
                  )}

                  {/* 참여자 목록 */}
                  {inspection.collaborators && inspection.collaborators.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">참여자</h3>
                      <div className="flex flex-wrap gap-2">
                        {inspection.collaborators.map((c) => (
                          <span
                            key={c.id}
                            className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                          >
                            {getActorDisplayName({ name: c.user.member?.name, deletedAt: c.user.deletedAt }, '-')} ({c.role === 'PRIMARY' ? '주담당' : '참여자'})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 생성일/수정일 */}
                  <div className="text-xs text-gray-400 pt-4 border-t">
                    <div>생성: {new Date(inspection.createdAt).toLocaleString('ko-KR')}</div>
                    <div>수정: {new Date(inspection.updatedAt).toLocaleString('ko-KR')}</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">점검을 찾을 수 없습니다.</div>
              )}
            </div>
        </Modal>
      )}

      {/* 결재 상황 모달 — 점검 상세 안의 버튼으로 그 위에 겹쳐 뜨므로 한 층 위(z-[80]) */}
      {approvalModalReportId && (
        <Modal
          open
          onClose={handleCloseApprovalModal}
          title="결재 상황"
          size="lg"
          panelClassName="!max-w-xl"
          overlayZClassName="z-[80]"
        >
            <div>
              {isApprovalLoading ? (
                <div className="text-center py-8 text-gray-500">로딩 중...</div>
              ) : approvalData?.data ? (
                <div className="space-y-4">
                  {/* 보고서 정보 */}
                  <div className="bg-gray-50 rounded-card p-4">
                    <div className="text-sm text-gray-500 mb-1">보고서</div>
                    <div className="font-medium">{(approvalData.data as ApprovalStatusData).reportTitle}</div>
                    <div className="mt-2">
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded ${
                          reportStatusColors[(approvalData.data as ApprovalStatusData).reportStatus] || 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {reportStatusLabels[(approvalData.data as ApprovalStatusData).reportStatus] || (approvalData.data as ApprovalStatusData).reportStatus}
                      </span>
                    </div>
                  </div>

                  {/* 라운드별 결재 목록 */}
                  {(approvalData.data as ApprovalStatusData).rounds.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      결재 정보가 없습니다.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {(approvalData.data as ApprovalStatusData).rounds.map((roundData, index) => {
                        const isLatestRound = index === 0;
                        const isExpanded = expandedRounds.has(roundData.round);

                        return (
                          <div key={roundData.round} className="border rounded-card overflow-hidden">
                            {/* 라운드 헤더 */}
                            <div
                              className={`px-4 py-3 flex items-center justify-between cursor-pointer ${
                                isLatestRound ? 'bg-brand-50' : 'bg-gray-50'
                              }`}
                              onClick={() => handleToggleRound(roundData.round)}
                            >
                              <div className="flex items-center gap-2">
                                <svg
                                  className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <span className="font-medium">
                                  라운드 {roundData.round}
                                  {isLatestRound && <span className="ml-2 text-xs text-brand-600">(현재)</span>}
                                </span>
                              </div>
                              {isLatestRound && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteRound((approvalData.data as ApprovalStatusData).reportId, roundData.round);
                                  }}
                                  disabled={deleteRoundMutation.isPending}
                                  className="px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-100 rounded disabled:opacity-50"
                                >
                                  라운드 삭제
                                </button>
                              )}
                            </div>

                            {/* 라운드 내용 */}
                            {isExpanded && (
                              <div className="border-t">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="bg-gray-50 text-xs text-gray-500">
                                      <th className="text-left px-4 py-2">결재 단계</th>
                                      <th className="text-left px-4 py-2">결재자</th>
                                      <th className="text-left px-4 py-2">상태</th>
                                      <th className="text-left px-4 py-2">결재일시</th>
                                      <th className="text-right px-4 py-2">액션</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {roundData.signatures.map((sig) => (
                                      <Fragment key={sig.id}>
                                        <tr className="border-b last:border-b-0">
                                          <td className="px-4 py-2">{sig.approvalLevel}차 결재</td>
                                          <td className="px-4 py-2">
                                            <div>{sig.signerName}</div>
                                            {sig.signerTitle && (
                                              <div className="text-xs text-gray-500">{sig.signerTitle}</div>
                                            )}
                                          </td>
                                          <td className="px-4 py-2">
                                            <span
                                              className={`px-2 py-0.5 text-xs font-medium rounded ${
                                                approvalStatusColors[sig.status] || 'bg-gray-100 text-gray-600'
                                              }`}
                                            >
                                              {approvalStatusLabels[sig.status] || sig.status}
                                            </span>
                                          </td>
                                          <td className="px-4 py-2 text-gray-500">
                                            {sig.signedAt
                                              ? new Date(sig.signedAt).toLocaleString('ko-KR', {
                                                  month: '2-digit',
                                                  day: '2-digit',
                                                  hour: '2-digit',
                                                  minute: '2-digit',
                                                })
                                              : '-'}
                                          </td>
                                          <td className="px-4 py-2 text-right">
                                            <button
                                              onClick={() => handleResetApproval(sig.id)}
                                              disabled={
                                                !canResetSignature(sig, roundData.signatures) ||
                                                resetApprovalMutation.isPending
                                              }
                                              className="px-2 py-1 text-xs text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                              title={
                                                sig.status === 'PENDING'
                                                  ? '이미 대기중인 결재입니다'
                                                  : !canResetSignature(sig, roundData.signatures)
                                                  ? '먼저 상위 결재를 초기화하세요'
                                                  : '결재 초기화'
                                              }
                                            >
                                              초기화
                                            </button>
                                          </td>
                                        </tr>
                                        {sig.comment && (
                                          <tr className="bg-gray-50">
                                            <td colSpan={5} className="px-4 py-2 text-xs text-gray-600">
                                              <span className="font-medium">코멘트:</span> {sig.comment}
                                            </td>
                                          </tr>
                                        )}
                                      </Fragment>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">결재 정보를 불러올 수 없습니다.</div>
              )}
            </div>
        </Modal>
      )}
      {confirmDialog}
    </div>
  );
}

/**
 * 점검 상세의 사진 섹션 — **P5가 요구한 자리**다.
 *
 * 「운영자가 admin의 ML 데이터 화면에서 같은 점검을 본다 → 지운 사진도 보이고, 각 사진에 품질
 * 등급이 표시된다. 전부 보여주되 무엇이 학습에 들어가는지를 알 수 있어야 한다.」
 *
 * ⚠️ 등급 분포 요약을 같이 둔다 — 사진이 많으면 타일을 하나씩 세는 것으로는 「무엇이 학습에
 * 들어가나」에 답할 수 없다. 여기 분포는 **이 점검의 사진**에서 센 것이고, 전조직 전수 분포는
 * ML 소스 관리 화면(`/ml-sources`)이 서버 카운트로 소유한다.
 *
 * ⚠️ **export인 것은 테스트 때문이다** — 분포 규칙(키 버킷·칩 순서)을 재려고 페이지 전체를
 * 렌더하면 라우터·쿼리·mock이 따라붙어 그 규칙이 아니라 배선을 재게 된다. 이 컴포넌트는
 * `photos` 하나만 받으므로 직접 렌더해 **그 규칙만** 잴 수 있다
 * (`__tests__/inspectionPhotosSection.test.tsx`).
 */
export function InspectionPhotosSection({
  photos,
  onLightboxOpenChange,
}: {
  photos: InspectionDetail['photos'];
  /** 라이트박스 열림·닫힘 통지 — 호스트 모달이 그동안 자기 Esc를 끈다(`Inspections`의 `openLightboxCount`). */
  onLightboxOpenChange?: (open: boolean) => void;
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  useLightboxOpenNotice(lightboxIndex !== null, onLightboxOpenChange);
  const urls = photos.map((p) => toAbsoluteApiFileUrl(p.url));
  const deletedCount = photos.filter((p) => p.deletedAt).length;

  // 이 점검 안의 등급 분포 — 등급이 실제로 등장한 것만 센다(0건 등급을 여기 나열할 이유가 없다.
  // 전수 분포에서 0건을 보여야 하는 것은 ML 화면 쪽 요구다).
  //
  // ⚠️ **`acc[p.grade]`로 바로 쓰지 말 것 — 등급이 없이 오면 키가 문자열 `"undefined"`가 되고,
  // 배지가 그 값을 그대로 그려 화면에 「undefined 7장」이 떴다**(2026-10-07 P4-h). 타입은 필수라
  // 적지만 배포 순서가 그 약속을 깬다(`PHOTO_GRADE_UNKNOWN_LABEL` JSDoc이 그 창을 숫자로 적는다).
  const gradeCounts = photos.reduce<Record<string, number>>((acc, p) => {
    const key = p.grade ?? PHOTO_GRADE_UNKNOWN_KEY;
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});

  /**
   * 칩 순서 — **알려진 등급은 정본 순서(A·B·C·D1·D2), 그 밖의 키는 뒤에 등장 순서로.**
   *
   * ⚠️ 종전은 `Object.entries(gradeCounts)`라 **사진 도착 순서**였고, 같은 분포가 `MlSources`의
   * `GradeSummary`와 다른 순서로 보였다(그쪽은 `ML_PHOTO_GRADES` 순서다).
   *
   * ⚠️ **`ML_PHOTO_GRADES.filter(...)`만 쓰면 안 된다** — 서버가 먼저 늘어나 새 등급 코드를
   * 보내거나 등급이 없이 오면 그 사진이 **분포에서 조용히 사라진다**(「그 등급이 0건이다」와
   * 「프론트가 빠뜨렸다」가 같은 모양이 되는, 이 코드베이스가 반복해 막아 온 실패다 —
   * `derivePurposeTabs` JSDoc의 성질 3). 그래서 나머지 키를 **뒤에 붙인다.**
   */
  const orderedGradeKeys = [
    ...ML_PHOTO_GRADES.filter((g) => gradeCounts[g]),
    ...Object.keys(gradeCounts).filter(
      (key) => !ML_PHOTO_GRADES.includes(key as MlPhotoGrade),
    ),
  ];

  return (
    <div className="border rounded-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">사진</h3>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>{photos.length}장</span>
          {deletedCount > 0 && <span className="text-red-500">(삭제 {deletedCount}장 포함)</span>}
        </div>
      </div>

      {photos.length === 0 ? (
        <div className="text-sm text-gray-500 text-center py-4">사진이 없습니다.</div>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {orderedGradeKeys.map((key) => (
              // `data-grade`는 **순서와 버킷 키를 잴 수 있게** 두는 것이다 — 칩 문구만으로는
              // 머리말의 「N장」·「삭제 N장 포함」과 섞여 계측기가 엉뚱한 것을 센다.
              <span
                key={key}
                data-testid="grade-chip"
                data-grade={key}
                className="inline-flex items-center gap-1"
              >
                {/* 등급이 없는 버킷은 배지에 `undefined`를 넘긴다 — 그 문구(「등급 없음」)의
                    소유자는 `PhotoGradeBadge`이고, 여기서 라벨을 또 정하지 않는다. */}
                <PhotoGradeBadge
                  grade={key === PHOTO_GRADE_UNKNOWN_KEY ? undefined : (key as MlPhotoGrade)}
                />
                <span className="text-xs text-gray-600">{gradeCounts[key]}장</span>
              </span>
            ))}
          </div>

          <ul className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {photos.map((photo, index) => (
              <li key={photo.id} className="border rounded overflow-hidden">
                <MlPhotoThumbnail
                  url={photo.url}
                  thumbnailUrl={photo.thumbnailUrl}
                  deletedAt={photo.deletedAt}
                  label={`사진 열기 (${index + 1}번째)`}
                  onClick={() => setLightboxIndex(index)}
                />
                <div className="p-1.5">
                  <PhotoGradeBadge grade={photo.grade} compact />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {lightboxIndex !== null && urls.length > 0 && (
        <ImageLightbox
          urls={urls}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

/**
 * 점검 상세의 개선 이미지 섹션 — **P11이 요구한 자리**다.
 *
 * 「운영자가 admin에서 그 개선 이미지를 본다 → 이미지와 요약은 보이고 **어느 보고서에서 왔는지는
 * 비어 있다**.」 그래서 `sourceReportId`가 null인 것을 빈칸으로 두지 않고 명시적으로 적는다 —
 * 문구는 `SOURCE_REPORT_DELETED_LABEL`로 ML 소스 화면과 공유한다(두 화면이 다른 말을 하면
 * 운영자가 둘을 다른 사건으로 읽는다).
 */
function InspectionImprovementImagesSection({
  images,
  onLightboxOpenChange,
}: {
  images: InspectionDetail['improvementImages'];
  onLightboxOpenChange?: (open: boolean) => void;
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  useLightboxOpenNotice(lightboxIndex !== null, onLightboxOpenChange);
  const urls = images.map((i) => toAbsoluteApiFileUrl(i.imageUrl));

  return (
    <div className="border rounded-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">개선 이미지</h3>
        <span className="text-xs text-gray-500">{images.length}건</span>
      </div>

      {images.length === 0 ? (
        <div className="text-sm text-gray-500 text-center py-4">개선 이미지가 없습니다.</div>
      ) : (
        <ul className="space-y-3">
          {images.map((image, index) => (
            <li key={image.id} className="flex gap-3">
              <button
                type="button"
                onClick={() => setLightboxIndex(index)}
                aria-label="개선 이미지 열기"
                className="shrink-0 w-20 h-20 bg-gray-100 rounded overflow-hidden"
              >
                <img
                  src={toAbsoluteApiFileUrl(image.imageUrl)}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-700 line-clamp-3">{image.summaryText}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <span>{image.aiModel}</span>
                  <span>· {image.usageCount}회 사용</span>
                  {!image.hasEmbedding && <span className="text-amber-600">· 임베딩 없음</span>}
                  {!image.sourceReportId && (
                    <span className="text-red-600">· {SOURCE_REPORT_DELETED_LABEL}</span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {lightboxIndex !== null && urls.length > 0 && (
        <ImageLightbox
          urls={urls}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

/**
 * 라이트박스가 열려 있는 동안 호스트에 「열림」을 알리고, 닫히거나 언마운트되면 「닫힘」을 알린다.
 * 호스트(점검 상세 base Modal)가 그동안 자기 Esc를 꺼 Esc 한 번에 라이트박스만 닫히게 한다
 * — 이유는 `Inspections`의 `openLightboxCount` 주석.
 */
function useLightboxOpenNotice(isOpen: boolean, onChange?: (open: boolean) => void) {
  useEffect(() => {
    if (!isOpen || !onChange) return;
    onChange(true);
    return () => onChange(false);
  }, [isOpen, onChange]);
}
